import Axios from "axios";
import Config from "../components/Config";

/**
 * Couche d'accès aux données de l'espace CP manager.
 *
 * Deux familles d'endpoints :
 *  - ceux qui existent déjà et sont utilisés tels quels (`/customer/managers`,
 *    `/customer/orders/:id`, `/customer/managers/bulk_receipts`) ;
 *  - ceux que le backend doit encore livrer (`/customer/manager/scope`,
 *    `/customer/manager/dashboard`). Ces derniers renvoient `null` au lieu de
 *    lever une erreur quand ils répondent 404, pour que l'UI puisse se replier
 *    sur un calcul côté client sans casser.
 *
 * Voir docs/BACKEND-TICKET-manager-dashboard.md pour le contrat attendu.
 */

/**
 * TVA belge appliquée aux commandes, en pourcentage.
 *
 * La maquette affichait 6 % ; c'est 21 %. Même taux et même méthode que
 * `src/setupProxy.js`, qui construit les factures Odoo — les deux doivent
 * rester d'accord, sinon le reçu affiché et la facture émise divergent.
 */
export const VAT_RATE = 21;

const VAT_MULTIPLIER = 1 + VAT_RATE / 100;

/**
 * Décompose un montant TTC.
 *
 * La TVA s'extrait du montant, elle ne s'y applique pas : sur 22,94 € TTC à
 * 21 %, la part de taxe est 22,94 − 22,94/1,21 = 3,98 €, et non 22,94 × 0,21.
 */
export const splitVat = (grossAmount) => {
  const gross = Number.parseFloat(grossAmount);
  if (!Number.isFinite(gross)) return { gross: 0, net: 0, vat: 0 };

  const net = gross / VAT_MULTIPLIER;
  return { gross, net, vat: gross - net };
};

/**
 * États d'une commande, dans l'ordre du flux.
 *
 * Ce sont les valeurs brutes de `orders.state`, sans regroupement : le manager
 * suit la commande dans le même vocabulaire que le reste de l'outil, et rien
 * ne se perd en traduction.
 *
 * `delivery` est l'état FINAL, pas un transit : il couvre 45 594 des 47 476
 * commandes prod des 12 derniers mois. `retrieved_by_user` (22 commandes) est
 * le retrait en boutique.
 */
export const ORDER_STATES = [
  "confirmed",
  "picked_up",
  "dropped_at_shop",
  "dropped_at_laundry",
  "processed",
  "ready_for_delivery",
  "ready_for_shop",
  "delivery",
  "retrieved_by_user",
];

/** États terminaux : la commande est rendue, il n'y a plus rien à suivre. */
export const CLOSED_STATES = ["delivery", "retrieved_by_user"];

/**
 * Parcours « boutique » : dépôt et retrait en magasin.
 *
 * Hors du périmètre d'un point de collecte, dont le linge est enlevé et livré
 * sur place. Les chiffres le confirment : sur les 47 476 commandes prod des 12
 * derniers mois, `dropped_at_shop` et `ready_for_shop` n'apparaissent pas une
 * seule fois, et `retrieved_by_user` couvre 22 commandes.
 *
 * Ces états restent affichables si une commande s'y trouve — on ne les propose
 * simplement pas comme filtre ni comme carte.
 */
export const SHOP_STATES = ["dropped_at_shop", "ready_for_shop", "retrieved_by_user"];

/** États pertinents pour un manager de point de collecte. */
export const MANAGER_STATES = ORDER_STATES.filter((state) => !SHOP_STATES.includes(state));

/** Une commande est « en cours » tant qu'elle n'est pas rendue. */
export const OPEN_STATES = ORDER_STATES.filter((state) => !CLOSED_STATES.includes(state));

export const isClosedState = (state) => CLOSED_STATES.includes(state);

/**
 * États où WOSH détient physiquement le linge.
 *
 * `confirmed` en est exclu — la commande est passée mais rien n'a été enlevé,
 * le linge est encore chez le client. Les états terminaux le sont aussi : il
 * est rendu. C'est ce que la rail de l'Overzicht doit suivre, le manager
 * voulant savoir ce qui est « chez nous » en ce moment.
 */
export const AT_LAUNDRY_STATES = MANAGER_STATES.filter(
  (state) => state !== "confirmed" && !CLOSED_STATES.includes(state)
);

export const isAtLaundry = (state) => AT_LAUNDRY_STATES.includes(state);

/** Position dans le flux, pour ordonner une timeline ou une rail. */
export const stateIndex = (state) => ORDER_STATES.indexOf(state);

const authHeaders = (authUser) => ({
  uid: authUser?.user_uid,
  client: authUser?.user_client,
  "access-token": authUser?.user_access_token,
});

const toNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Un produit compte comme « au kilo » si l'API le marque explicitement, ou si
 * son libellé porte une unité de poids. Le second cas couvre les payloads plus
 * anciens qui n'exposent pas le drapeau.
 */
const isKiloProduct = (product) => {
  if (product?.kilo_product || product?.act_as_kilo) return true;
  const label = `${product?.name || ""} ${product?.name_nl || ""} ${product?.name_fr || ""}`;
  return /\bkg\b|\bkilo/i.test(label);
};

/**
 * Nettoie le libellé d'une pièce pesée.
 *
 * L'API concatène le nom de la pièce, sa quantité et le produit au kilo
 * parent : « Tablecloth (large) 1 - kg Laundry ». On coupe à partir du
 * nombre suivi d'un tiret.
 *
 * L'ancienne page découpait sur le seul caractère « 1 », ce qui amputait
 * « Bed sheet (2pers) 1 - kg Laundry » ou tout nom contenant un 1. Exiger le
 * motif « chiffre espace tiret » évite ces faux positifs.
 */
export const cleanPieceName = (name) => {
  // Un champ absent le reste : le transformer en chaîne vide ferait passer une
  // traduction manquante pour une traduction vide, et le repli sur `name`
  // n'aurait plus lieu.
  if (typeof name !== "string") return name;
  const cleaned = name.replace(/\s+\d+\s*-\s.*$/, "").trim();
  return cleaned || name.trim();
};

/**
 * Détail des pièces contenues dans une prestation au kilo.
 *
 * Un produit `act_as_kilo` (« Linge au kilo », « Repassage par kilo ») porte un
 * `product_detail` qui énumère chaque pièce pesée. On les regroupe par produit
 * pour afficher « 3 × Polo » plutôt que trois lignes identiques.
 *
 * L'ancienne page tronquait le libellé sur le caractère « 1 » ; les noms en
 * base sont propres (« Polo », « Pantalon », « Drap de lit 2p ») et ce
 * découpage mutilait tout nom contenant un 1. Le libellé est donc gardé tel quel.
 */
export const kiloBreakdown = (products = []) => {
  const byProduct = new Map();

  products.forEach((product) => {
    if (!product?.act_as_kilo) return;

    (product.product_detail || []).forEach((piece) => {
      const key = piece?.product_id ?? piece?.name;
      if (key == null) return;

      const existing = byProduct.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        byProduct.set(key, {
          id: key,
          name: cleanPieceName(piece.name),
          name_fr: cleanPieceName(piece.name_fr),
          name_nl: cleanPieceName(piece.name_nl),
          count: 1,
        });
      }
    });
  });

  return [...byProduct.values()].sort((a, b) => b.count - a.count);
};

export const kilosFromProducts = (products = []) =>
  products.reduce(
    (sum, product) => (isKiloProduct(product) ? sum + toNumber(product?.quantity) : sum),
    0
  );

/**
 * Identifiant WOSH visible par le client (ex. « MG47783 »).
 *
 * L'endpoint manager n'expose pas `wosh_user_id`, mais `wosh_order_id` est
 * construit comme `<woshUserId>_<n>` : le préfixe est donc l'identifiant
 * cherché. Renvoie `null` si la référence ne suit pas ce format — notamment
 * le repli sur un `order_id` purement numérique, où un préfixe n'aurait
 * aucun sens.
 */
const woshUserIdFromReference = (reference) => {
  const match = /^([A-Za-z]+\d+)_/.exec(String(reference ?? ""));
  return match ? match[1] : null;
};

/**
 * Normalise une ligne de `/customer/managers` vers la forme consommée par l'UI.
 * Les champs absents du schéma actuel (chambre, service) restent `null` : les
 * composants les omettent proprement plutôt que d'afficher un espace vide.
 */
export const normalizeOrder = (raw = {}) => {
  const pickupDate = toDate(raw.pickup_date);
  const deliveryDate = toDate(raw.delivery_date);
  const state = raw.status || raw.state || null;

  return {
    id: raw.order_id ?? raw.id,
    reference: raw.wosh_order_id || raw.order_id || raw.id,
    // Le nom du champ n'est pas garanti : `/customer/managers` ne l'expose pas
    // sous `user_id` sur tous les environnements, et sans lui toute écriture
    // sur le résident part avec l'identifiant WOSH et revient en 403.
    residentId: raw.user_id ?? raw.resident_id ?? raw.customer_id ?? raw.userId ?? null,
    woshUserId: raw.wosh_user_id ?? woshUserIdFromReference(raw.wosh_order_id),
    residentName: raw.user_full_name || raw.user_name || "",
    room: raw.room_number ?? raw.room ?? null,
    department: raw.department ?? null,
    state,
    paymentStatus: raw.payment_status || null,
    pickupDate,
    deliveryDate,
    // Horodatages réels des transitions, quand l'API les expose. `processed_at`
    // est de loin le mieux rempli (64 % des commandes prod) ; `state_updated_at`
    // date le passage dans l'état courant et sert de repli pour l'étape en cours.
    pickedUpAt: toDate(raw.picked_up_at),
    processedAt: toDate(raw.processed_at),
    deliveredAt: toDate(raw.delivered_at),
    stateUpdatedAt: toDate(raw.state_updated_at),
    total: toNumber(raw.subtotal ?? raw.total ?? raw.price),
    kilos: raw.kilos != null ? toNumber(raw.kilos) : null,
    bags: raw.bags_count != null ? toNumber(raw.bags_count) : null,
    isLate: !isClosedState(state) && deliveryDate != null && deliveryDate < startOfToday(),
  };
};

export const startOfToday = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
};

export const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

/**
 * Liste des commandes du point de collecte.
 * `signal` permet d'annuler la requête précédente lors d'une recherche débouncée.
 */
export const fetchOrders = async ({ authUser, page = 1, perPage = 25, search, state, pickupDate, deliveryDate, signal }) => {
  const params = { page, per_page: perPage };
  if (search) params.search = String(search).trim();
  if (state) params.state = state;
  if (pickupDate) params.pickup_date = pickupDate;
  if (deliveryDate) params.delivery_date = deliveryDate;

  const response = await Axios({
    url: `${Config.apiURL}/customer/managers`,
    method: "GET",
    params,
    headers: authHeaders(authUser),
    signal,
  });

  return {
    orders: (response?.data?.data || []).map(normalizeOrder),
    totalPages: response?.data?.pagination?.total_pages || 1,
    totalCount: response?.data?.pagination?.total_count ?? null,
  };
};

/**
 * Récupère toutes les pages, dans la limite de `maxPages`.
 *
 * Solution transitoire : les agrégats de l'Overzicht (totaux kilos, répartition
 * par statut) ne sont pas calculables depuis une page de 25 lignes. Dès que
 * `/customer/manager/dashboard` existe, cette fonction n'est plus appelée pour
 * le dashboard. La borne évite de marteler l'API sur les gros points de collecte.
 */
export const fetchAllOrders = async ({ authUser, maxPages = 8, perPage = 100, signal, ...rest }) => {
  const first = await fetchOrders({ authUser, page: 1, perPage, signal, ...rest });
  const pages = Math.min(first.totalPages, maxPages);
  let orders = first.orders;

  for (let page = 2; page <= pages; page += 1) {
    const next = await fetchOrders({ authUser, page, perPage, signal, ...rest });
    orders = orders.concat(next.orders);
  }

  return {
    orders,
    truncated: first.totalPages > pages,
    totalPages: first.totalPages,
  };
};

/**
 * Déballe la commande de `/customer/orders/:id`.
 *
 * Exporté pour être testable : c'est un point de défaillance silencieuse —
 * viser la mauvaise clé ne lève aucune erreur, l'écran affiche simplement
 * une commande vide à 0 €.
 */
export const extractOrderDetail = (payload) =>
  payload?.order || payload?.data?.order || payload?.data || payload || {};

export const fetchOrderDetail = async ({ authUser, orderId, signal }) => {
  const response = await Axios({
    url: `${Config.apiURL}/customer/orders/${orderId}`,
    method: "GET",
    headers: authHeaders(authUser),
    signal,
  });

  // L'endpoint renvoie la commande sous la clé `order`. Les deux replis
  // suivants couvrent les variantes plus anciennes du payload ; sans eux on
  // retombe sur un objet sans `products` et l'écran affiche « aucun article »
  // avec un total à zéro, sans la moindre erreur.
  const data = extractOrderDetail(response?.data);
  const products = data.products || [];

  const serviceFee = toNumber(data.service_fee);
  const promotion = toNumber(data.promotion);
  const itemsTotal = products.reduce(
    (sum, product) => sum + toNumber(product?.product_price) * toNumber(product?.quantity),
    0
  );

  return {
    id: orderId,
    products,
    pickedUpAt: toDate(data.picked_up_at),
    processedAt: toDate(data.processed_at),
    deliveredAt: toDate(data.delivered_at),
    stateUpdatedAt: toDate(data.state_updated_at),
    bags: (data.bags || []).filter((bag) => toNumber(bag?.quantity) > 0),
    kilos: kilosFromProducts(products),
    kiloPieces: kiloBreakdown(products),
    serviceFee,
    promotion,
    itemsTotal,
    // Le reçu doit s'additionner à l'écran : le total découle des lignes
    // affichées, pas d'un champ `price` qui ne les reflète pas.
    grossTotal: itemsTotal + serviceFee - promotion,
    events: data.events || null,
    // Le détail d'une commande est la seule source qui puisse encore porter
    // l'identifiant numérique du résident : la liste `/customer/managers` ne le
    // sérialise pas, et sans lui aucune écriture sur le résident n'aboutit.
    residentId:
      data.user_id ?? data.resident_id ?? data.customer_id ?? data.user?.id ?? data.customer?.id ?? null,
  };
};

export const requestBulkReceipts = async ({ authUser, orderIds }) => {
  const response = await Axios({
    url: `${Config.apiURL}/customer/managers/bulk_receipts`,
    method: "POST",
    data: { order_ids: orderIds },
    headers: authHeaders(authUser),
  });

  return response?.data?.url || null;
};

/** Renvoie `null` sur 404 : l'endpoint n'est pas encore livré par le backend. */
const optionalGet = async ({ authUser, path, params, signal }) => {
  try {
    const response = await Axios({
      url: `${Config.apiURL}${path}`,
      method: "GET",
      params,
      headers: authHeaders(authUser),
      signal,
    });
    return response?.data?.data || response?.data || null;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
};

/**
 * Profil du manager connecté.
 *
 * `company_name` n'est jamais recopié dans le store à l'authentification — le
 * seul endroit qui l'expose est cet endpoint. C'est pourtant le nom de
 * l'établissement, affiché dans la sidebar et imprimé sur les étiquettes.
 */
export const fetchProfile = async ({ authUser, signal }) => {
  const response = await Axios({
    url: `${Config.apiURL}/customer/profiles`,
    method: "GET",
    headers: authHeaders(authUser),
    signal,
  });

  const profile = response?.data?.profile || {};
  return {
    companyName: profile.company_name || "",
    firstName: profile.first_name || "",
    lastName: profile.last_name || "",
  };
};

/**
 * Met à jour l'identité d'un résident.
 *
 * `PUT /auth` ne modifie que l'utilisateur connecté : rien ne permet
 * aujourd'hui à un manager d'éditer un de ses résidents. L'appel vise donc un
 * endpoint qui reste à livrer, et distingue explicitement son absence (404)
 * d'un vrai échec — l'écran peut ainsi dire ce qui manque au lieu d'un
 * « erreur » opaque. Voir le point 9 du ticket backend.
 */
/**
 * Met en forme l'erreur renvoyée par le serveur.
 *
 * Rails répond de plusieurs façons selon l'endroit où la validation échoue :
 * une chaîne, `{ error }`, `{ message }`, `{ errors: ["..."] }`, ou le format
 * ActiveRecord `{ errors: { champ: ["..."] } }`. L'ancienne version interpolait
 * directement `body.error`, ce qui affichait « [object Object] » dès que le
 * corps était structuré — soit exactement les cas où le message compte.
 */
export const describeServerError = (error) => {
  const status = error?.response?.status;
  const body = error?.response?.data;

  const flatten = (value) => {
    if (value == null) return null;
    if (typeof value === "string") return value.trim() || null;
    if (Array.isArray(value)) return value.map(flatten).filter(Boolean).join(", ") || null;
    if (typeof value === "object") {
      const parts = Object.entries(value)
        .map(([field, detail]) => {
          const text = flatten(detail);
          return text ? `${field}: ${text}` : null;
        })
        .filter(Boolean);
      return parts.length ? parts.join(" · ") : null;
    }
    return String(value);
  };

  const message =
    flatten(body?.errors) || flatten(body?.error) || flatten(body?.message) || flatten(body);

  if (!status) return error?.message || null;
  return `HTTP ${status}${message ? ` — ${message}` : ""}`;
};

export const updateResident = async ({ authUser, residentId, firstName, lastName }) => {
  try {
    const response = await Axios({
      url: `${Config.apiURL}/customer/manager/residents/${residentId}`,
      method: "PUT",
      data: { first_name: firstName, last_name: lastName },
      headers: authHeaders(authUser),
    });
    return { ok: true, data: response?.data ?? null };
  } catch (error) {
    if (error?.response?.status === 404) return { ok: false, reason: "not-implemented" };
    // Le serveur refuse d'écrire sur un résident qui a une commande en cours et
    // répond 403 « Access Denied », sans dire laquelle des deux règles a joué.
    // C'est de loin la plus fréquente : l'autre, le hors-périmètre, ne peut pas
    // arriver depuis un écran qui ne liste que les résidents du manager.
    if (error?.response?.status === 403) return { ok: false, reason: "locked" };
    return { ok: false, reason: "failed", detail: describeServerError(error) };
  }
};

/**
 * Le renommage est livré et actif depuis le 17 août 2026 : il ne porte que
 * l'identifiant du résident, rien d'autre n'est nécessaire.
 *
 * La création a été rouverte le 18 août, `/customer/manager/scope` étant livré
 * sur staging : le formulaire dispose enfin du `collection_point_id` que le
 * serveur exige pour déduire la société. C'est ce qui manquait au 422.
 *
 * La constante reste en place — elle referme la fonctionnalité proprement si le
 * périmètre venait à disparaître d'un environnement.
 */
export const RESIDENT_CREATE_ENABLED = true;

/**
 * Crée un résident rattaché à la société du manager.
 *
 * L'email n'est pas demandé : sur les 9 180 résidents en production, 76 % en
 * portent un généré automatiquement. Le backend sait donc déjà en fabriquer un,
 * et l'exiger du manager n'aurait pas de sens pour un résident de maison de
 * repos qui ne se connectera jamais. Il reste transmis s'il est fourni.
 *
 * `wosh_user_id` est également attribué côté serveur : les 9 180 en ont un.
 */
export const createResident = async ({
  authUser,
  firstName,
  lastName,
  email,
  woshUserId,
  collectionPointId,
}) => {
  try {
    const response = await Axios({
      url: `${Config.apiURL}/customer/manager/residents`,
      method: "POST",
      data: {
        first_name: firstName,
        last_name: lastName,
        ...(email ? { email } : {}),
        // Identifiant choisi par le manager. Absent, le serveur génère le sien
        // — c'est le comportement actuel et il reste le défaut.
        ...(woshUserId ? { wosh_user_id: woshUserId } : {}),
        // Uniquement par identifiant : c'est le serveur qui en déduit la
        // société, après avoir vérifié que le manager en est responsable. Un
        // nom libre laisserait rattacher un résident à n'importe quel
        // établissement, y compris celui d'un autre manager.
        ...(collectionPointId != null ? { collection_point_id: collectionPointId } : {}),
      },
      headers: authHeaders(authUser),
    });
    return { ok: true, data: response?.data?.resident || response?.data || null };
  } catch (error) {
    if (error?.response?.status === 404) return { ok: false, reason: "not-implemented" };
    // Le serveur refuse d'écrire sur un résident qui a une commande en cours et
    // répond 403 « Access Denied », sans dire laquelle des deux règles a joué.
    // C'est de loin la plus fréquente : l'autre, le hors-périmètre, ne peut pas
    // arriver depuis un écran qui ne liste que les résidents du manager.
    if (error?.response?.status === 403) return { ok: false, reason: "locked" };
    return { ok: false, reason: "failed", detail: describeServerError(error) };
  }
};

/**
 * Annuaire des résidents du point de collecte.
 *
 * Livré le 18 août. Jusque-là la liste se déduisait des commandes, ce qui
 * rendait invisible tout résident qui n'en avait pas encore passé — dont, par
 * construction, celui qu'on vient de créer.
 *
 * L'enveloppe exacte n'a pas encore été observée : on accepte les formes
 * plausibles et on signale en développement une réponse non vide dont rien ne
 * se tire, plutôt que d'afficher silencieusement une liste vide.
 */
export const normalizeResidents = (payload) => {
  if (!payload) return [];

  const list = Array.isArray(payload)
    ? payload
    : payload.residents || payload.users || payload.data;

  if (!Array.isArray(list) || !list.length) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[manager] annuaire reçu mais illisible :", payload);
    }
    return [];
  }

  return list
    .map((raw) => {
      const first = raw.first_name || raw.firstName || "";
      const last = raw.last_name || raw.lastName || "";
      const name = raw.full_name || raw.name || `${first} ${last}`.trim();
      const id = raw.user_id ?? raw.resident_id ?? raw.customer_id ?? raw.userId ?? raw.id ?? null;
      const woshUserId = raw.wosh_user_id || raw.woshUserId || null;

      return { id, key: id ?? woshUserId, woshUserId, name, firstName: first, lastName: last };
    })
    .filter((resident) => resident.name || resident.woshUserId);
};

export const fetchResidents = async ({ authUser, signal }) => {
  const payload = await optionalGet({ authUser, path: "/customer/manager/residents", signal });
  const residents = normalizeResidents(payload);

  if (process.env.NODE_ENV !== "production") {
    const sample = Array.isArray(payload) ? payload[0] : payload?.residents?.[0] || payload?.users?.[0];
    // eslint-disable-next-line no-console
    console.info(
      "[manager] annuaire :",
      residents.length,
      "résidents — champs du premier élément :",
      sample ? Object.keys(sample) : "(aucun)",
      "— identifiant retenu :",
      residents[0]?.id
    );
  }

  return residents;
};

/**
 * Retire un résident du point de collecte.
 *
 * `DELETE` est la méthode exposée, mais la destruction réelle est peu probable
 * et n'est de toute façon pas à notre main : toutes les clés étrangères qui
 * pointent vers `users` sont en `NO ACTION`, donc supprimer un résident qui a
 * des commandes lèverait une violation côté base plutôt que d'effacer son
 * historique de facturation. `users` porte par ailleurs `archived_at` et
 * `deleted_at`. C'est le serveur qui tranche ; on rapporte ce qu'il répond.
 */
export const removeResident = async ({ authUser, residentId }) => {
  try {
    const response = await Axios({
      url: `${Config.apiURL}/customer/manager/residents/${residentId}`,
      method: "DELETE",
      headers: authHeaders(authUser),
    });
    return { ok: true, data: response?.data ?? null };
  } catch (error) {
    if (error?.response?.status === 404) return { ok: false, reason: "not-implemented" };
    // Le serveur refuse d'écrire sur un résident qui a une commande en cours et
    // répond 403 « Access Denied », sans dire laquelle des deux règles a joué.
    // C'est de loin la plus fréquente : l'autre, le hors-périmètre, ne peut pas
    // arriver depuis un écran qui ne liste que les résidents du manager.
    if (error?.response?.status === 403) return { ok: false, reason: "locked" };
    return { ok: false, reason: "failed", detail: describeServerError(error) };
  }
};

export const fetchScope = ({ authUser, signal }) =>
  optionalGet({ authUser, path: "/customer/manager/scope", signal });

export const fetchDashboard = ({ authUser, collectionPointId, period, signal }) =>
  optionalGet({
    authUser,
    path: "/customer/manager/dashboard",
    params: { collection_point_id: collectionPointId, period },
    signal,
  });

export const fetchResident = ({ authUser, residentId, signal }) =>
  optionalGet({ authUser, path: `/customer/manager/residents/${residentId}`, signal });

export const isAbortError = (error) =>
  error?.name === "AbortError" || error?.name === "CanceledError" || error?.code === "ERR_CANCELED";
